'use client'

/**
 * The feedback-pin overlay (Issue #13578, epic #13573).
 *
 * A portal layer over the current page that renders one marker per feedback
 * anchor captured on this route. Positions come from a live
 * `getBoundingClientRect` on the element F2's `resolveSelectorPath` finds;
 * when it finds nothing, from the stored `pinRect` rescaled to the current
 * viewport, with a "moved" badge.
 *
 * ## Where the anchors come from
 *
 * A PROP, not an import. F1 (#13574) owns the fetch and is unmerged; an
 * import of a `listRouteAnchors` that does not exist would not typecheck.
 * `FeedbackPinAnchor` in `src/lib/feedback/pin-anchor.ts` is the declared
 * seam, and `FeedbackPinAnchorLoader` beside it is the shape F1's action has
 * to satisfy, so mounting this under the feedback-mode toggle (#13577) once
 * both land is passing two props.
 *
 * Because anchors arrive as a prop and are filtered by `anchorsForRoute` on
 * every render, edge case 4 ("route change → overlay clears before refetch;
 * no stale pins") is a property of the data rather than of an effect's
 * timing: the instant `route` changes, last route's anchors stop matching.
 *
 * ## Re-layout
 *
 * Three triggers, because pages move for three different reasons: `scroll`
 * and `resize` (listened to in the CAPTURE phase, so a scrolling inner
 * container is seen too — `scroll` does not bubble), and a `MutationObserver`
 * for everything else, debounced because an expanding section emits dozens of
 * mutations in a frame.
 *
 * TWO guards stop the observer feeding itself: mutations inside the overlay
 * are ignored, and a measurement identical to the current one does not call
 * `setState`. The second is the one that actually holds — React portals
 * reconcile into `document.body`, so a marker re-render IS a body mutation.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { resolveSelectorPath } from '@/lib/feedback/selector-path'
import {
  anchorsForRoute,
  scalePinRect,
  type FeedbackPinAnchor,
  type FeedbackPinAnchorLoader,
} from '@/lib/feedback/pin-anchor'
import {
  clusterPins,
  filterHiddenPins,
  pinAnchorPoint,
  type PinViewportBounds,
  type PositionedPin,
} from '@/components/feedback/pin-cluster'
import { FeedbackPin } from '@/components/feedback/feedback-pin'
// The attribute, from the module that DEFINES it — the same import the mode
// banner makes. A second spelling of `data-feedback-mode-exempt` would be a
// string that looks right and opts nothing out.
import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from '@/components/feedback/feedback-mode-attributes'
import { trackFeedbackPinOpened } from '@/app/actions/feedback-triage'
import { log } from '@/lib/logger'
import { ERROR_IDS } from '@/lib/errors/constants'
import { serializeError } from '@/lib/errors/serialize-error'

/**
 * Mutation debounce. Long enough to coalesce one section's expand animation,
 * short enough that a pin does not visibly lag the content it points at.
 */
const MUTATION_DEBOUNCE_MS = 150

/**
 * How much of the top of the viewport the fixed app header stack covers, in
 * CSS px (Issue #13652).
 *
 * MEASURED, not the `--header-height: 4rem` constant in `globals.css`. The
 * sticky stack in `horizontal-header.tsx` is the header PLUS whatever banners
 * are above it — impersonation, demo mode, presentation mode — so the
 * constant is right only when none of them is showing, and a pin on an
 * impersonated session would sit behind the banner. The header's own
 * `bottom` already includes anything stacked above it.
 *
 * `Math.max(0, ...)` because the breadcrumb layout renders a NON-sticky
 * header, which scrolls away: once its `bottom` is negative it occludes
 * nothing, and treating that as an inset would drag every marker upward.
 *
 * SCOPED to `[data-app-header]`, never a bare `header` tag. `src/` carries 38
 * `<header>` elements and most are content-section headers, while the app bar
 * is mounted per page (`HorizontalHeaderWrapper`) rather than in a root
 * layout — so the first `<header>` in document order is not reliably the
 * chrome. The failure was not graceful: a content header partway down the
 * page with `bottom = 900` makes `minY = 944`, which collapses `maxY` to the
 * same value and draws EVERY marker below the fold, so the overlay silently
 * shows nothing.
 *
 * Note the obvious alternative does not work: rejecting a candidate with
 * `rect.top > 0` would reject the real app header, because
 * `horizontal-header.tsx` puts `sticky top-0` on a wrapper div and the
 * `<header>` sits below the banner stack inside it — which is exactly why
 * reading its `bottom` picks the banners up correctly.
 *
 * Falls back to 0 when the page has no app header at all, which still leaves
 * the marker's own body clamped into the viewport.
 */
function measureHeaderInset(): number {
  const header = document.querySelector('header[data-app-header]')
  if (!header) return 0
  return Math.max(0, header.getBoundingClientRect().bottom)
}

export interface PinOverlayProps {
  /**
   * Anchors, supplied directly. Filtered to `route` here, so they may safely
   * be stale. Takes precedence over {@link PinOverlayProps.loadAnchors} when
   * both are given.
   */
  anchors?: readonly FeedbackPinAnchor[]
  /**
   * F1's fetch, injected. Called with the current route on mount and again on
   * every route change. This is the seam #13574's action plugs into; until it
   * lands, callers pass `anchors` instead.
   *
   * MUST be referentially stable — wrap it in `useCallback`. A new identity
   * re-runs the fetch effect, which clears every pin before refetching, so an
   * inline arrow produces a visible pin flash on every parent render.
   */
  loadAnchors?: FeedbackPinAnchorLoader
  /** The current route, e.g. from `usePathname()`. */
  route: string
  /** Whether the viewer may triage from a popover. */
  isAdmin?: boolean
  /** Viewer's timezone for the thread's timestamps. */
  timezone?: string
  /**
   * How many pins on this route could not be rendered because their element
   * is not laid out (edge case 1), and WHICH ones (#13653). The feedback-mode
   * banner badges the count and lists the pins, so a thread inside a
   * collapsed section is still discoverable and still openable.
   *
   * The second argument was added by #13653 rather than a second callback:
   * a count-only caller keeps working unchanged, and the count and the list
   * cannot disagree because they are one report. The array is identity-
   * stable while the hidden SET is unchanged — a scroll does not re-report.
   */
  onHiddenCountChange?: (count: number, pins: readonly PositionedPin[]) => void
  /** Called after a triage lands, so the parent can refetch anchors. */
  onTriaged?: () => void
  /**
   * Published whenever a pin's thread opens or closes (#13788). The mount
   * forwards it to `feedback-mode-context`, which is what suspends the picker
   * for as long as a panel is up.
   */
  onThreadOpenChange?: (open: boolean) => void
  /**
   * A `?feedback=<id>` landing (#13788). The pin holding it opens itself, and
   * the element it points at is scrolled into view — once.
   */
  autoOpenFeedbackId?: string | null
  /** Called the one time {@link autoOpenFeedbackId} opened a pin. */
  onDeepLinkOpened?: (feedbackId: string) => void
  /**
   * Whether to draw at all (#13756). Defaults to `true`.
   *
   * The DECISION belongs to the caller — `pin-overlay-mount.tsx` derives it
   * from feedback mode, the viewer's show/hide choice and whether the picker
   * is mid-selection — but the enforcement has to be here, because "hidden"
   * for a `fixed inset-0` layer must mean UNMOUNTED rather than transparent.
   * An invisible layer is still in the hit test `element-picker.tsx` runs, so
   * hiding it with opacity would leave a pin as the thing under the cursor of
   * somebody choosing what to give feedback on.
   *
   * It deliberately does NOT stop the fetch: turning pins back on is a paint,
   * not a read, and gating the loader on it would blank and repaint the
   * overlay every time the picker stopped previewing an element. The mount
   * gates the READ on feedback mode instead, one level up.
   */
  visible?: boolean
}

/**
 * Is the hidden set unchanged, as far as a reader of it could tell (#13653)?
 *
 * Deliberately NOT {@link samePositions}: a hidden pin's `x`/`y` are never
 * drawn, but they still move with scroll on the fallback path, so comparing
 * them would re-report — and re-render every context reader — on every
 * scroll frame for a list whose contents had not changed.
 */
function sameHiddenPins(
  a: readonly PositionedPin[],
  b: readonly PositionedPin[],
): boolean {
  if (a.length !== b.length) return false
  return a.every((p, i) => {
    const q = b[i]
    return (
      p.anchor.feedbackId === q.anchor.feedbackId &&
      p.anchor.status === q.anchor.status &&
      p.anchor.replyCount === q.anchor.replyCount &&
      p.anchor.summary === q.anchor.summary &&
      p.resolved === q.resolved
    )
  })
}

/**
 * Is `next` indistinguishable from what is already rendered?
 *
 * This exists ONLY to stop the MutationObserver feeding itself (a portal
 * re-render is a `document.body` mutation), so it has to be true whenever a
 * re-measure found nothing new. It must NOT be true when the anchor DATA
 * changed: `PositionedPin` embeds the whole anchor, so returning the previous
 * array keeps the previous `status`, `replyCount` and `summary` on screen.
 *
 * That is not hypothetical. `onTriaged` refetches after a triage, which
 * changes `status` (and `replyCount`, since the note becomes a comment)
 * without moving the element — so comparing geometry alone made this PR's own
 * refresh path a no-op on any page that had not reflowed.
 *
 * The payload fields are compared explicitly rather than by object identity.
 * Identity would also work today, but it makes the loop guard depend on the
 * caller never re-creating an equal anchor array, which is a property of the
 * caller's memoization rather than of this component.
 */
function samePositions(
  a: readonly PositionedPin[],
  b: readonly PositionedPin[],
): boolean {
  if (a.length !== b.length) return false
  return a.every((p, i) => {
    const q = b[i]
    return (
      p.anchor.feedbackId === q.anchor.feedbackId &&
      p.anchor.status === q.anchor.status &&
      p.anchor.replyCount === q.anchor.replyCount &&
      p.anchor.summary === q.anchor.summary &&
      p.x === q.x &&
      p.y === q.y &&
      p.resolved === q.resolved &&
      p.visible === q.visible
    )
  })
}

const NO_ANCHORS: readonly FeedbackPinAnchor[] = []
const NO_PINS: readonly PositionedPin[] = []

export function PinOverlay({
  anchors,
  loadAnchors,
  route,
  isAdmin = false,
  timezone = 'UTC',
  onHiddenCountChange,
  onTriaged,
  onThreadOpenChange,
  autoOpenFeedbackId,
  onDeepLinkOpened,
  visible = true,
}: PinOverlayProps) {
  const [fetched, setFetched] =
    useState<readonly FeedbackPinAnchor[]>(NO_ANCHORS)
  /**
   * Monotonic token identifying the in-flight fetch. A response is applied
   * only when it is still the latest request — without it, a slow fetch for
   * `/home` resolving after the user has navigated to `/today` would repaint
   * the previous page's pins over the new one, which is edge case 4 arriving
   * by the back door.
   */
  const requestRef = useRef(0)

  useEffect(() => {
    if (!loadAnchors) return
    const token = ++requestRef.current
    // Clear FIRST, then fetch: "overlay clears before refetch; no stale pins".
    setFetched(NO_ANCHORS)
    loadAnchors(route)
      .then(result => {
        if (requestRef.current !== token) return
        setFetched(result)
      })
      .catch((error: unknown) => {
        // A rejected fetch leaves the overlay empty, which is already the
        // state set above — but it must not become an unhandled rejection.
        // Logged rather than toasted: feedback mode annotates someone else's
        // page, so a failure to load pins is not worth interrupting whatever
        // they were doing.
        if (requestRef.current !== token) return
        log.error(ERROR_IDS.FEEDBACK_PIN_ANCHORS_LOAD_FAILED, {
          action: 'PinOverlay.loadAnchors',
          route,
          error: serializeError(error),
        })
      })
  }, [loadAnchors, route])

  const source = anchors ?? fetched

  const routeAnchors = useMemo(
    () => anchorsForRoute(source, route),
    [source, route],
  )
  const [positioned, setPositioned] = useState<PositionedPin[]>([])
  const [isMounted, setIsMounted] = useState(false)
  const overlayRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const measure = useCallback(() => {
    // Nothing to position, so measure nothing (Issue #13652). The effects
    // below deliberately install no listeners when there are no anchors, and
    // `measureHeaderInset()` forces a layout — paying that reflow for an
    // empty map breaks the "install nothing when there is nothing to
    // position" property the rest of this component keeps.
    if (routeAnchors.length === 0) {
      setPositioned(prev => (prev.length === 0 ? prev : []))
      return
    }

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    }
    // The box every marker has to stay inside (#13652). Measured ONCE per
    // pass rather than per anchor: `getBoundingClientRect` on the header
    // forces layout, and this runs on every scroll frame.
    //
    // `documentElement.clientWidth/Height`, NOT `window.innerWidth/Height`.
    // The overlay layer is `fixed inset-0`, so its box is the initial
    // containing block, which EXCLUDES a classic scrollbar; `innerWidth`
    // includes it. On a platform with classic scrollbars (Windows and Linux
    // Chrome, macOS with "Show scroll bars: Always") clamping to
    // `innerWidth - 22` leaves the marker's right ~15px in the scrollbar
    // gutter — the same partial clipping this clamp exists to remove. The
    // `viewport` above keeps `innerWidth/Height` because `scalePinRect`
    // compares it against a capture-time viewport, which is a different
    // measurement.
    //
    // Falls back to the `viewport` numbers when `clientWidth`/`clientHeight`
    // read 0, which is what a document with no layout reports — happy-dom in
    // the unit tests, and a real browser before first layout. Clamping to a
    // width of 0 would collapse every marker onto `minX`, so "no layout
    // information" must not be spent as "a zero-width viewport".
    const docEl = document.documentElement
    const bounds: PinViewportBounds = {
      width: docEl.clientWidth || viewport.width,
      height: docEl.clientHeight || viewport.height,
      topInset: measureHeaderInset(),
    }

    const next: PositionedPin[] = routeAnchors.map(anchor => {
      const el = resolveSelectorPath(anchor.selectorPath, document)
      if (el) {
        const box = el.getBoundingClientRect()
        const rect = {
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
        }
        const point = pinAnchorPoint(rect, bounds)
        return {
          anchor,
          x: point.x,
          y: point.y,
          resolved: true,
          // A zero-area rect is an element that exists but is not laid out —
          // a collapsed section, a closed tab panel. Rendering it would put
          // the marker at 0,0, over the page header, pointing at nothing.
          visible: rect.width > 0 || rect.height > 0,
        }
      }

      const rect = scalePinRect(
        anchor.pinRect,
        anchor.capturedViewport,
        viewport,
      )
      // `PinRect` is DOCUMENT-relative (see its JSDoc); the resolved branch
      // above measures with `getBoundingClientRect`, which is VIEWPORT-
      // relative, and the overlay layer is `fixed`. So the stored rect has to
      // be converted or the two branches write different coordinate spaces
      // into the same field: a "moved" pin would land its full scroll offset
      // too low AND stay glued to the screen while every resolved pin
      // scrolled past it.
      //
      // The conversion happens HERE, before the clamp, and not to the point
      // afterwards: `bounds` describes the viewport, so clamping a document
      // coordinate against it would bound the pin by the wrong origin and
      // then shift the result by the scroll offset — reintroducing the
      // overflow the clamp exists to remove on any scrolled page.
      const point = pinAnchorPoint(
        {
          ...rect,
          top: rect.top - window.scrollY,
          left: rect.left - window.scrollX,
        },
        bounds,
      )
      return {
        anchor,
        x: point.x,
        y: point.y,
        resolved: false,
        visible: rect.width > 0 || rect.height > 0,
      }
    })

    setPositioned(current => (samePositions(current, next) ? current : next))
  }, [routeAnchors])

  // Layout effect, not effect: measuring after paint would show every marker
  // at its previous position for one frame on a route change.
  //
  // `visible` is a dependency rather than a guard: the positions go stale
  // while the layer is hidden (the page scrolls, the route's DOM changes),
  // so showing again has to re-measure before the first paint or every marker
  // appears at where it was when the viewer pressed Hide.
  useLayoutEffect(() => {
    if (!visible) return
    measure()
  }, [measure, visible])

  useEffect(() => {
    // Nothing to position: install no page-wide listeners at all. Without
    // this an overlay with zero anchors still ran `measure()` on every scroll
    // frame of every scrolling container on the page.
    //
    // `!visible` is the second such state, added with the Show/Hide control
    // in #13756: anchors are fetched and kept (hiding is a paint, not a
    // refetch) while nothing is drawn, so the listener would run a
    // full-document `resolveSelectorPath` per anchor per frame for a layer
    // the viewer has switched off.
    if (!visible || routeAnchors.length === 0) return

    // Coalesced to one measurement per frame. `measure()` calls
    // `resolveSelectorPath` per anchor, and an attribute path is a
    // `document.querySelectorAll` while a `::text(...)` path additionally
    // normalises the text of every candidate — a full-document scan per
    // anchor. Scroll fires far more often than once per frame, and a marker
    // cannot be repainted more often than that anyway.
    let frame: number | undefined
    const onScroll = () => {
      if (frame !== undefined) return
      frame = requestAnimationFrame(() => {
        frame = undefined
        measure()
      })
    }

    // Capture phase: `scroll` does not bubble, so a pin inside a scrolling
    // pane would never re-measure with a bubble-phase listener.
    window.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll, { capture: true })
      window.removeEventListener('resize', onScroll)
    }
  }, [measure, routeAnchors.length, visible])

  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return
    // Same reason as the scroll effect, and a sharper one: with nothing
    // rendered `overlayRef.current` is null, so the `fromOverlay` guard below
    // can never be true and EVERY mutation anywhere on the page scheduled a
    // debounced no-op.
    //
    // `!visible` reaches that state by the other route (#13756), and reaches
    // it for the whole time the pins are switched off inside feedback mode —
    // while the picker rewrites its highlight's inline styles on every mouse
    // move, which is exactly the mutation storm this guard exists to sit out.
    if (!visible || routeAnchors.length === 0) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new MutationObserver(records => {
      const fromOverlay = records.every(r => {
        const node = r.target
        return (
          overlayRef.current !== null &&
          node instanceof Node &&
          overlayRef.current.contains(node)
        )
      })
      if (fromOverlay) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => measure(), MUTATION_DEBOUNCE_MS)
    })
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    })
    return () => {
      if (timer) clearTimeout(timer)
      observer.disconnect()
    }
  }, [measure, routeAnchors.length, visible])

  // Held in state so its identity only changes when the hidden SET does —
  // see `sameHiddenPins`. The report is what the banner lists (#13653).
  const [hiddenPins, setHiddenPins] =
    useState<readonly PositionedPin[]>(NO_PINS)
  useEffect(() => {
    const next = filterHiddenPins(positioned)
    setHiddenPins(current => (sameHiddenPins(current, next) ? current : next))
  }, [positioned])
  useEffect(() => {
    onHiddenCountChange?.(hiddenPins.length, hiddenPins)
  }, [hiddenPins, onHiddenCountChange])

  const clusters = useMemo(() => clusterPins(positioned), [positioned])

  /**
   * Scroll the deep-linked thread's element into view (#13788).
   *
   * Best effort by construction: the selector may no longer resolve, which is
   * edge case 1 — the overlay already draws the pin at its remembered
   * position with the "moved" badge, and the popover already explains it. So
   * a miss here is a no-op rather than an error path, and nothing is added to
   * report it.
   *
   * The ref makes it once per landing. The measurement effects above re-run on
   * every scroll frame, and a scroll that re-triggered itself would fight the
   * viewer for the scroll position for as long as the panel stayed open.
   */
  const scrolledToRef = useRef<string | null>(null)
  useEffect(() => {
    if (!visible || autoOpenFeedbackId == null) return
    if (scrolledToRef.current === autoOpenFeedbackId) return
    const target = routeAnchors.find(a => a.feedbackId === autoOpenFeedbackId)
    if (!target) return
    scrolledToRef.current = autoOpenFeedbackId
    const element = resolveSelectorPath(target.selectorPath, document)
    element?.scrollIntoView?.({ block: 'center' })
  }, [visible, autoOpenFeedbackId, routeAnchors])

  // Nothing to show, nothing mounted. A permanently mounted full-viewport
  // layer is a pointer-events trap one CSS regression away from swallowing
  // every click on the page — and since #13756 the same sentence is the
  // pin-visibility rule: told not to be visible, this draws no layer and no
  // markers rather than a transparent one the picker would still hit-test.
  //
  // AFTER the hooks above, never before them: the measurement effects are
  // unconditional, so a `visible` that flips mid-session cannot change the
  // hook order.
  if (!isMounted || !visible || clusters.length === 0) return null

  return createPortal(
    <div
      ref={overlayRef}
      data-testid="feedback-pin-overlay"
      // #13756 — feedback-mode chrome opts out of being picked, exactly as the
      // banner and the picker's own control bar do. The picker disables its
      // capture layer for a single `elementFromPoint` call, so without this the
      // element under the cursor can be a PIN and the viewer anchors their
      // feedback to somebody else's marker. This is the rule that makes the
      // z-index below safe rather than a way to break element picking.
      {...{ [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' }}
      //
      // ## `z-feedback-pins`, not `z-40` (#13756, re-banded by #16841 and
      // moved onto sai-ui's layer scale by #16990; it was `z-545`)
      //
      // The overlay now renders ONLY inside feedback mode, and the picker's
      // capture layer is `fixed inset-0 z-feedback-picker` covering the
      // viewport. At
      // `z-40` every pointer press aimed at a pin landed on that layer
      // instead, and the picker then discarded it — `handleClick` resolves
      // the marker, finds the exempt ancestor above, and returns. So inside
      // the mode a pin could be seen and never opened by mouse or finger;
      // only the keyboard reached the popover, which is why the golden-path
      // spec stayed green over it.
      //
      // `z-feedback-pins` puts the markers above the capture layer
      // (`z-feedback-picker`) and below the banner and recorder
      // (`z-feedback-banner`) and `FeedbackModal` (`z-feedback-modal`), so
      // the stacking order of the mode's own chrome is unchanged. #16841
      // lifted the whole band above ordinary modals, so a pin anchored inside
      // an open dialog (`z-modal`) or menu (`z-menu`) is drawn over it.
      // Only the markers become interactive, because of the `pointer-events`
      // split below: everything between them still passes through to the
      // picker.
      //
      // THE TRADE, stated because a static z-order cannot avoid it and the
      // other side of it is somebody's bug report either way (cross-model
      // review, #13760). A marker sits at its target's top-right corner and
      // takes the pointer THERE, so an element cannot be picked on the few
      // pixels its own pin occupies — and an element SMALLER than the marker
      // cannot be picked at all while it carries one. The rule chosen is the
      // annotation-tool one: the dot opens the thread, the rest of the
      // element is still the element. The alternative — leaving the markers
      // under the capture layer — makes every pin unopenable by mouse or
      // finger for the whole of the mode, which is the strictly larger hole
      // and the one #13760's review caught.
      //
      // `pointer-events-none` on the layer, re-enabled on each marker: the
      // overlay covers the whole viewport and must not intercept clicks on
      // the page it is annotating.
      className="fixed inset-0 z-feedback-pins pointer-events-none"
    >
      {clusters.map(cluster => (
        <FeedbackPin
          key={cluster.id}
          cluster={cluster}
          isAdmin={isAdmin}
          timezone={timezone}
          onOpened={(feedbackId, clusterSize) => {
            void trackFeedbackPinOpened({ feedbackId, clusterSize })
          }}
          onTriaged={onTriaged}
          onThreadOpenChange={onThreadOpenChange}
          autoOpenFeedbackId={autoOpenFeedbackId}
          onDeepLinkOpened={onDeepLinkOpened}
        />
      ))}
    </div>,
    document.body,
  )
}
